import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { Button, Checkbox, Select, Table } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'

import { CursoModel } from '../../models/CursoModel'
import api from '../../api'
import { useCursosDispatch, cursosStore } from '../../stores/cursosStore'
import { TextFieldSearch } from '../../components/TextField'

type CursosFilters = {
  searchText: string
  apenasAtivos: boolean
  categoria: string | null
}

const INITIAL_FILTERS: CursosFilters = {
  searchText: '',
  apenasAtivos: false,
  categoria: null,
}

const normalizeForSearch = (value: string | number | null | undefined) =>
  String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const CursosView: React.FC = (props: any) => {
  const dispatch = useCursosDispatch()
  const [allCursos, setAllCursos] = useState<CursoModel[]>([])
  const [filters, setFilters] = useState<CursosFilters>(INITIAL_FILTERS)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15 })
  const [loading, setLoading] = useState(false)

  const fetchAllCursos = useCallback(async () => {
    setLoading(true)
    try {
      const rows = (await api.Cursos.getAll({ includeSiteMetadata: true })) || []
      rows.sort((a, b) => (a.id || 0) - (b.id || 0))

      setAllCursos(rows)
      dispatch(cursosStore.actions.cursosGetSuccess({ rows, count: rows.length }))
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    fetchAllCursos()
  }, [fetchAllCursos])

  const categoriaOptions = useMemo(() => {
    const categories = allCursos.map((curso) => (curso.categorias || '').trim()).filter(Boolean)

    return Array.from(new Set(categories)).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [allCursos])

  const filteredCursos = useMemo(() => {
    const normalizedSearch = normalizeForSearch(filters.searchText)

    return allCursos.filter((curso) => {
      if (filters.apenasAtivos && !curso.siteMetadata) return false
      if (filters.categoria && curso.categorias !== filters.categoria) return false

      if (!normalizedSearch) return true

      const searchableFields = normalizeForSearch(
        [
          String(curso.id || ''),
          curso.nome || '',
          curso.nomeReduzido || '',
          curso.categorias || '',
        ].join(' ')
      )

      return searchableFields.includes(normalizedSearch)
    })
  }, [allCursos, filters])

  const paginatedCursos = useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize
    const end = start + pagination.pageSize
    return filteredCursos.slice(start, end)
  }, [filteredCursos, pagination.current, pagination.pageSize])

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredCursos.length / pagination.pageSize))
    if (pagination.current > totalPages) {
      setPagination((prev) => ({ ...prev, current: totalPages }))
    }
  }, [filteredCursos.length, pagination.current, pagination.pageSize])

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Nome', dataIndex: 'nome', key: 'nome', width: 640 },
    {
      title: 'Dias para emissão do certificado',
      dataIndex: 'diasParaEmissaoCertificado',
      key: 'diasParaEmissaoCertificado',
      width: 210,
      render: (value) => value ?? '-',
    },
    {
      title: 'Anos de Validade do Curso',
      dataIndex: 'validadeCurso',
      key: 'validadeCurso',
      width: 180,
      render: (value) => value ?? '-',
    },
    {
      title: 'Carga Horária',
      dataIndex: 'cargaHoraria',
      key: 'cargaHoraria',
      width: 110,
      render: (value) => value ?? '-',
    },
    {
      title: 'Categoria do Curso',
      dataIndex: 'categorias',
      key: 'categorias',
      width: 180,
      render: (value) => value || '-',
    },
    {
      title: 'Qtd de módulos',
      key: 'quantidadeModulos',
      width: 140,
      render: (_, curso: CursoModel) => curso.modulos?.length ?? 0,
    },
    {
      title: 'Ações',
      key: 'action',
      width: 90,
      render: () => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation()
            // deleteCurso
          }}
        />
      ),
    },
  ]

  const setFilterAndResetPage = (nextFilters: Partial<CursosFilters>) => {
    setFilters((prev) => ({ ...prev, ...nextFilters }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS)
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleTableChange = (pagination: any) => {
    setPagination({
      current: pagination?.current ?? 1,
      pageSize: pagination?.pageSize ?? 15,
    })
  }

  const handleCreateCurso = () => props.history.push('/cursos/novo')
  const handleOpenCurso = (cursoId: number) => props.history.push(`/cursos/${cursoId}/editar`)

  return (
    <Layout>
      <Container>
        <CursosHeader onCreateCurso={handleCreateCurso} />

        <CursosFiltersPanel
          filters={filters}
          categoriaOptions={categoriaOptions}
          onClearFilters={handleClearFilters}
          onChangeFilters={setFilterAndResetPage}
        />

        <CursosTable
          columns={columns}
          dataSource={paginatedCursos}
          loading={loading}
          pagination={pagination}
          total={filteredCursos.length}
          onChangePagination={handleTableChange}
          onOpenCurso={handleOpenCurso}
        />
      </Container>
    </Layout>
  )
}

const CursosHeader = ({ onCreateCurso }: { onCreateCurso: () => void }) => (
  <PageHeader>
    <Title>Cursos</Title>
    <Button type="primary" onClick={onCreateCurso}>
      Criar novo curso
    </Button>
  </PageHeader>
)

const CursosFiltersPanel = ({
  filters,
  categoriaOptions,
  onClearFilters,
  onChangeFilters,
}: {
  filters: CursosFilters
  categoriaOptions: string[]
  onClearFilters: () => void
  onChangeFilters: (nextFilters: Partial<CursosFilters>) => void
}) => (
  <FiltersContainer>
    <FiltersHeader>
      <FiltersTitle>Filtros</FiltersTitle>
      <Button type="text" onClick={onClearFilters}>
        Limpar filtros
      </Button>
    </FiltersHeader>

    <FiltersRow>
      <TextFieldSearch
        value={filters.searchText}
        placeholder="Pesquisar por ID, nome, nome reduzido ou categoria"
        onChange={(e) => onChangeFilters({ searchText: e.target.value })}
      />

      <Select
        allowClear
        showSearch
        placeholder="Categoria do Curso"
        style={{ minWidth: 240 }}
        value={filters.categoria ?? undefined}
        onChange={(value) => onChangeFilters({ categoria: value || null })}
        options={categoriaOptions.map((categoria) => ({
          value: categoria,
          label: categoria,
        }))}
        optionFilterProp="label"
      />

      <Checkbox
        checked={filters.apenasAtivos}
        onChange={(e) => onChangeFilters({ apenasAtivos: e.target.checked })}
      >
        Cursos ativos no site
      </Checkbox>
    </FiltersRow>
  </FiltersContainer>
)

const CursosTable = ({
  columns,
  dataSource,
  loading,
  pagination,
  total,
  onChangePagination,
  onOpenCurso,
}: {
  columns: any[]
  dataSource: CursoModel[]
  loading: boolean
  pagination: { current: number; pageSize: number }
  total: number
  onChangePagination: (pagination: any) => void
  onOpenCurso: (cursoId: number) => void
}) => (
  <TableWrapper>
    <Table
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      rowKey="id"
      scroll={{ x: 1450 }}
      locale={{ emptyText: 'Não há nenhum curso cadastrado ainda.' }}
      onRow={(curso) => ({
        onClick: () => onOpenCurso(curso.id),
        style: { cursor: 'pointer' },
      })}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '15', '20', '50'],
        showTotal: (allItems, range) => `${range[0]}-${range[1]} de ${allItems} itens`,
      }}
      onChange={onChangePagination}
    />
  </TableWrapper>
)

const Layout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  max-width: calc(100vw - 20px);

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const FiltersContainer = styled.div`
  padding: 16px 20px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
`

const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`

const FiltersTitle = styled.span`
  font-size: 16px;
  color: #494d50;
  font-weight: 600;
`

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`

const TableWrapper = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 8px;
  overflow-x: auto;

  .ant-table-thead > tr > th {
    background: #ffffff;
    color: #585858;
    font-weight: normal;
    border-bottom: 1px solid #e7e3ec;
    padding: 12px 16px;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e7e3ec;
    padding: 8px 16px;
  }

  .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-tbody > tr.ant-table-row.ant-table-row-hover > td {
    background: #eeeeee;
  }
`
